/**
 * Onboarding Tutorial Component
 * Step-by-step guided tour for first-time users
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Progress } from './ui/progress';
import {
  Heart,
  TrendingUp,
  Calendar,
  LineChart,
  Target,
  Sparkles,
  ArrowRight,
  ArrowLeft,
  X,
  CheckCircle2,
  Zap,
  Shield,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  image?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface OnboardingTutorialProps {
  onComplete: () => void;
  onSkip: () => void;
}

export function OnboardingTutorial({ onComplete, onSkip }: OnboardingTutorialProps) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isCompleting, setIsCompleting] = useState(false);

  const steps: OnboardingStep[] = [
    {
      id: 'welcome',
      title: 'Welcome to TENSION! 🎉',
      description: 'Thanks for joining our beta! TENSION helps you understand and improve your relationship through daily check-ins and AI-powered insights. Let\'s take a quick tour to get you started.',
      icon: <Sparkles className="w-12 h-12 text-purple-600" />,
    },
    {
      id: 'rti-scoring',
      title: '5-Component RTI Scoring',
      description: 'Your Relationship Tension Index (RTI) tracks 5 key areas: Communication, Trust, Intimacy, Alignment, and Stress. Each component is scored 0-100, giving you a complete picture of your relationship health.',
      icon: <TrendingUp className="w-12 h-12 text-blue-600" />,
    },
    {
      id: 'daily-checkins',
      title: 'Daily Check-ins',
      description: 'Complete a quick 2-minute assessment each day. Answer honest questions about your relationship, and we\'ll track your RTI over time. Consistency is key to getting accurate insights!',
      icon: <Calendar className="w-12 h-12 text-green-600" />,
      action: {
        label: 'Try Your First Check-in',
        onClick: () => navigate('/check-in'),
      },
    },
    {
      id: 'insights',
      title: 'Personalized Action Cards',
      description: 'Based on your check-ins, you\'ll receive personalized action cards with practical tips to improve specific areas. These are tailored to your relationship dynamics and change as your needs evolve.',
      icon: <Target className="w-12 h-12 text-orange-600" />,
    },
    {
      id: 'timeline',
      title: 'Timeline Visualization',
      description: 'Track your relationship journey over time with beautiful charts and graphs. See patterns, celebrate improvements, and identify areas that need attention. Your data stays private and secure.',
      icon: <LineChart className="w-12 h-12 text-indigo-600" />,
      action: {
        label: 'View Dashboard',
        onClick: () => navigate('/dashboard'),
      },
    },
    {
      id: 'privacy',
      title: 'Privacy-first by design',
      description:
        'Your relationship data is sensitive. Core check-ins stay on your device by default (browser storage). Sign-in and any optional cloud sync use encrypted connections (TLS); sync is a deliberate choice if you want your data on multiple devices. We do not sell your data—see the Privacy Policy for the full picture.',
      icon: <Shield className="w-12 h-12 text-green-600" />,
    },
    {
      id: 'singles-mode',
      title: 'Singles & Couples Mode',
      description: 'Whether you\'re in a relationship or single, TENSION adapts to you. Singles mode focuses on self-awareness and relationship readiness, while couples mode tracks relationship dynamics.',
      icon: <Users className="w-12 h-12 text-pink-600" />,
      action: {
        label: 'Choose Your Mode',
        onClick: () => navigate('/settings'),
      },
    },
    {
      id: 'ready',
      title: 'You\'re All Set! 🚀',
      description: 'You\'re ready to start your relationship journey with TENSION. Remember to complete daily check-ins for the best results. As a beta tester, your feedback is invaluable—help us build something amazing!',
      icon: <CheckCircle2 className="w-12 h-12 text-green-600" />,
    },
  ];

  const progress = ((currentStep + 1) / steps.length) * 100;
  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (!isFirstStep) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleComplete = async () => {
    setIsCompleting(true);
    
    // Mark tutorial as completed
    localStorage.setItem('onboarding_completed', 'true');
    localStorage.setItem('onboarding_completed_at', new Date().toISOString());
    
    toast.success('Tutorial completed! Welcome to TENSION 🎉');
    
    setTimeout(() => {
      onComplete();
    }, 500);
  };

  const handleSkipTutorial = () => {
    if (confirm('Are you sure you want to skip the tutorial? You can always access it later from Settings.')) {
      localStorage.setItem('onboarding_skipped', 'true');
      localStorage.setItem('onboarding_skipped_at', new Date().toISOString());
      toast.info('Tutorial skipped. Access it anytime from Settings → Tutorial');
      onSkip();
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' && !isLastStep) {
        handleNext();
      } else if (e.key === 'ArrowLeft' && !isFirstStep) {
        handlePrevious();
      } else if (e.key === 'Escape') {
        handleSkipTutorial();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentStep]);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Heart className="w-6 h-6 text-purple-600" />
              <span className="font-semibold text-neutral-900">
                Getting Started Guide
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSkipTutorial}
              className="text-neutral-500 hover:text-neutral-900"
            >
              <X className="w-4 h-4 mr-1" />
              Skip Tutorial
            </Button>
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-neutral-600">
                Step {currentStep + 1} of {steps.length}
              </span>
              <span className="text-neutral-600">{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          <div className="text-center mb-8">
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              {step.icon}
            </div>
            
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              {step.title}
            </h2>
            
            <p className="text-lg text-neutral-600 leading-relaxed max-w-lg mx-auto">
              {step.description}
            </p>
          </div>

          {/* Optional Action Button */}
          {step.action && (
            <div className="flex justify-center mb-6">
              <Button
                variant="outline"
                onClick={step.action.onClick}
                className="border-purple-200 text-purple-700 hover:bg-purple-50"
              >
                <Zap className="w-4 h-4 mr-2" />
                {step.action.label}
              </Button>
            </div>
          )}

          {/* Step Indicators */}
          <div className="flex justify-center gap-2 mb-8">
            {steps.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentStep
                    ? 'bg-purple-600 w-8'
                    : index < currentStep
                    ? 'bg-purple-300'
                    : 'bg-neutral-200'
                }`}
                aria-label={`Go to step ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-neutral-50">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={isFirstStep}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>

            <div className="text-sm text-neutral-500">
              Press <kbd className="px-2 py-1 bg-white border rounded text-xs">←</kbd>{' '}
              <kbd className="px-2 py-1 bg-white border rounded text-xs">→</kbd> to navigate
            </div>

            <Button
              onClick={handleNext}
              disabled={isCompleting}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            >
              {isLastStep ? (
                isCompleting ? (
                  'Completing...'
                ) : (
                  <>
                    Get Started
                    <CheckCircle2 className="w-4 h-4 ml-2" />
                  </>
                )
              ) : (
                <>
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
